import "./polyfills";
import { platformBrowserDynamic } from "@angular/platform-browser-dynamic";
import { ConfigServiceCommand, PlatformName, SetStartupContextPayload } from "@fabric-types/svc-config";
import { EventBus } from "@obg/event-bus";
import { EventBusCommand } from "@obg/event-bus/types";
import { JourneyHandler } from "@obg/journey-handler";
import {
	getStartupContext,
	ObgAppStartup,
	ObgNodeContext,
	RumConfig,
	ServerUtils,
	STARTUP_CONTEXT_TOKEN,
	OBG_EVENT_BUS,
} from "@obg/ngx.core";
import { initStorage, isLocalStorageSupported, REGISTER_KEY_DEPENDENCY_TOKEN } from "@obg/ngx.global";
import { METRICS_CLIENT_HTTP, ObgEventBus } from "@obg/ngx.global.evo";
import { NODE_LOGGER_SERVICE_TOKEN, REQUEST_TOKEN, RESPONSE_TOKEN } from "@obg/ngx.universal-express";
import { AwsRum, AwsRumConfig } from "aws-rum-web";

import { AppClientModule } from "./app/app.client.module";
import { AppDOMServerModule } from "./app/app.dom-server.module";

declare const window: {
	nodeContext?: ObgNodeContext;
	serverUtils?: ServerUtils;
	obgStartup?: ObgAppStartup;
	localStorage: LocalForage;
	location: Location;
	isDOMSSR: boolean;
};

declare const document: {
	obgEventBus: ObgEventBus;
} & Document;

(async () => {
	await initStorage();
	document.obgEventBus ??= new EventBus();
	const nodeContext = window?.nodeContext;
	if (!nodeContext) {
		throw new Error("Node context was not received.");
	}

	const utils = window.serverUtils;
	let startup = window.obgStartup;

	const customerId = (isLocalStorageSupported() ? localStorage.getItem("OptiGUID") : "") ?? "";

	const [config, mfes, routes, gdprCountries, translatedExternalRoutes, optimizelyFeatureFlags] =
		await Promise.allSettled([
			startup?.config || fetch("/configuration").then((x) => x.json()),
			startup?.mfes || fetch("/mfes").then((x) => x.json()),
			startup?.routes || fetch("/routes").then((x) => x.json()),
			startup?.gdprCountries || fetch("/gdpr-countries").then((x) => x.json()),
			startup?.translatedExternalRoutes || fetch("/translated-external-routes").then((x) => x.json()),
			!window.isDOMSSR
				? fetch(
						`/optimizely-feature-flags?${new URLSearchParams({
							context: nodeContext.detectedMarket.code,
							id: customerId,
						})}`
					).then((x) => x.json())
				: {},
		]).then((results) => results.map((result) => (result.status === "fulfilled" ? result.value : {})));

	if (!startup) {
		startup = {};
	}

	startup.optimizelyInfo = optimizelyFeatureFlags.info;

	if (config.journey) {
		const journeyHandler = new JourneyHandler(config.journey, document);

		document.obgEventBus.listen(EventBusCommand.AllCommands, (data) => journeyHandler.onGlobalEvent(data.command));
	}

	const startupContext = getStartupContext(
		nodeContext,
		config,
		gdprCountries,
		routes,
		translatedExternalRoutes,
		mfes,
		optimizelyFeatureFlags.flags
	);

	if (config?.appSettings?.mfeConfig) {
		document.obgEventBus?.dispatch(ConfigServiceCommand.UpdateJurisdiction, {
			jurisdiction: config.core.jurisdiction.default,
		});

		document.obgEventBus?.dispatch(ConfigServiceCommand.UpdateConfig, {
			config: {
				...config.appSettings.mfeConfig,
				"svc-content": {
					...config.appSettings.mfeConfig["svc-content"],
					content: {
						...config.appSettings.mfeConfig["svc-content"].content,
						brandId: config.core.appContext.apiToken,
						marketCode: config.core.market.languageCode,
					},
				},
				"svc-request": {
					...config.appSettings.mfeConfig["svc-request"],
					baseUrl: config.core.appContext.apiBaseUri,
				},
				"svc-realtime": {
					...config.appSettings.mfeConfig["svc-realtime"],
					signalr: {
						customerTransmitEndpoint: config.core.signalr.customerTransmitEndpoint,
						groupTransmitEndpoint: config.core.signalr.groupTransmitEndpoint,
						isAcknowledgementEnabled: config.core.signalr.isAcknowledgementEnabled,
					},
				},
				"app-wallet": {
					...config.appSettings.mfeConfig["app-wallet"],
					currency: {
						defaultCurrencyCode: config.core.currency.defaultCurrencyCode,
						currencyCodesByCountryCode: config.core.currency.currencyCodesByCountryCode,
					},
				},
				"app-gaming": {
					...config.appSettings.mfeConfig["app-gaming"],
					casinoTable: {
						providers: config.gaming.casinoTable.providers,
					},
					providerJackpots: {
						enabled: config.gaming.providerJackpots?.providerJackpots?.enabled ?? true,
					},
					userGames: {
						favourites: {
							gameIdsHiddenFromFavourites: config.gaming.favourites?.gameIdsHiddenFromFavourites || [],
						},
						lastPlayed: {
							gameIdsHiddenFromLastPlayed: config.gaming.userGames?.gameIdsHiddenFromLastPlayed || [],
						},
					},
					jackpot: {
						enabled: config.gaming.jackpot?.enabled ?? false,
					},
					gameLauncher: {
						gameLauncherAppUrl: config.gaming.gameLauncher.gameLauncherAppUrlDefault,
						scrollableGames: [],
						scrollableProviders: [],
						providerAllowAttributes: {},
					},
					latestWinners: {
						isEnabled: config.gaming.latestWinners?.isEnabled ?? false,
						gameIdsHiddenFromLatestWinners:
							config.gaming.latestWinners?.gameIdsHiddenFromLatestWinners || [],
					},
					playForFunOptions: {
						loggedIn: config.gaming.playForFunOptions?.loggedIn ?? false,
						loggedOut: config.gaming.playForFunOptions?.loggedIn ?? true,
					},
					thumbnailActionMapping: {
						gameaction: "action",
						play: "login",
					},
				},
				"app-offers": {
					...config.appSettings.mfeConfig["app-offers"],
				},

				"app-payments": {
					...config.appSettings.mfeConfig["app-payments"],
				},
				"svc-platform": {
					...config.appSettings.mfeConfig["svc-platform"],
					language: "",
					version: "",
					oneTrust: {
						cookieDomain: config.common?.oneTrust?.cookieDomain || "",
					},
					cookieExpiryYears: config.common?.dataProtection?.cookieExpiryYears ?? 2,
					...(globalThis.isDOMSSR ? { awsThreatDetection: { isEnabled: false } } : {}),
				},
				"svc-telemetry": {
					isGtmEnabled: true,
				},
			},
		});

		document.obgEventBus?.dispatch(ConfigServiceCommand.SetStartupContext, {
			context: {
				activeExperimentList: nodeContext.activeExperiments,
				appVersion: nodeContext.version,
				brandId: config.core.appContext.apiToken,
				brandName: startupContext.brandName,
				operatingSystem: getPlatform(),
				countryCode: nodeContext.countryCode,
				deviceType: nodeContext.deviceType,
				deviceExperience: nodeContext.deviceExperience,
				isBot: nodeContext.isBot,
				isInternal: nodeContext.isInternal,
				isSearchEngine: nodeContext.isSiteMapCrawler,
				marketCode: nodeContext.detectedMarket.code,
				availableMarkets: config.appSettings.marketList,
				currentMarket: startupContext.market,
				isServer: false,
				isBrowser: true,
				environment: startupContext.environment,
			},
		} satisfies SetStartupContextPayload);
	}

	await Promise.allSettled(Object.keys(mfes).map((mfeName) => loadMfe(mfeName))).then((results) =>
		results.forEach((result) => {
			if (result.status === "rejected") {
				console.warn("Loading of microFrontends failed!", result.reason);
			}
		})
	);

	async function loadMfe(mfeName: string) {
		// Horse racing is loaded once it can be used
		if (globalThis.isDOMSSR || mfeName === "app-horse-racing") return;

		const es6Module =
			mfes[mfeName].jsPath.endsWith(".js") || mfes[mfeName].jsPath.endsWith(".mjs")
				? mfes[mfeName].jsPath
				: mfes[mfeName].jsPath + "/esm/loader.js";
		try {
			// @ts-ignore
			const module = await import(/* webpackIgnore: true */ es6Module);
			if (module && module.defineCustomElements) {
				module.defineCustomElements();
			}
		} catch (error) {
			console.error(`Failed to load mfe ${mfeName}:`, error.message);
		}
	}

	if (config.appSettings.rum?.isEnabled && !utils) {
		createRumClient(
			config.appSettings.brandName,
			nodeContext.version,
			config.appSettings.environment,
			config.appSettings.rum
		);
	}

	platformBrowserDynamic([
		{
			provide: OBG_EVENT_BUS,
			useValue: document.obgEventBus,
		},
		{
			provide: STARTUP_CONTEXT_TOKEN,
			useValue: startupContext,
		},
		{ provide: REQUEST_TOKEN, useValue: utils?.request },
		{ provide: RESPONSE_TOKEN, useValue: utils?.response },
		{ provide: NODE_LOGGER_SERVICE_TOKEN, useValue: utils?.loggerService },
		{ provide: METRICS_CLIENT_HTTP, useValue: utils?.httpClientMetrics },
		{ provide: REGISTER_KEY_DEPENDENCY_TOKEN, useValue: utils?.registerKey },
	]).bootstrapModule(utils ? AppDOMServerModule : AppClientModule);
})();

function createRumClient(brand: string, siteVersion: string, env: string, rum: RumConfig) {
	try {
		if (!rum.cfg) {
			return;
		}

		const config: AwsRumConfig = {
			sessionSampleRate: rum.cfg.sessionSampleRate,
			guestRoleArn: rum.cfg.guestRoleArn,
			identityPoolId: rum.cfg.identityPoolId,
			endpoint: rum.cfg.endpoint,
			telemetries: rum.cfg.telemetries,
			allowCookies: rum.cfg.allowCookies,
			enableXRay: rum.cfg.enableXRay,
			// limited by 10 total, invalid will be dropped
			// docs https://docs.aws.amazon.com/AmazonCloudWatch/latest/monitoring/CloudWatch-RUM-custom-metadata.html
			sessionAttributes: {
				siteVersion,
				env,
				brand,
			},
		};

		if (!rum.cfg.app) {
			return;
		}

		const APPLICATION_ID = rum.cfg.app.id;
		const APPLICATION_VERSION = rum.cfg.app.version;
		const APPLICATION_REGION = rum.cfg.app.region;

		const awsRum: AwsRum = new AwsRum(APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, config);
		awsRum.enable();
	} catch {
		// Ignore errors thrown during CloudWatch RUM web client initialization
	}
}

function isBrowser() {
	return typeof window !== "undefined";
}

function getPlatform() {
	if (!isBrowser()) {
		return PlatformName.Unknown;
	}

	const userAgent = navigator.userAgent || navigator.vendor;

	if (/windows phone/i.test(userAgent)) {
		return PlatformName.WindowsPhone;
	}

	if (/android/i.test(userAgent)) {
		return PlatformName.Android;
	}

	// iOS pre 13 (iPad|iPhone|iPod) - MacIntel (iPad iOS 13)
	if (/iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
		return PlatformName.IOS;
	}

	if (/windows nT|xP/i.test(userAgent)) {
		return PlatformName.Windows;
	}

	if (/mac os x (\d+(\.?_?\d+)+)/i.test(userAgent)) {
		return PlatformName.MacOSX;
	}

	return PlatformName.Unknown;
}
